import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import InputIconByKey from './InputIconByKey';
import RequiredAsteriskTooltip from './RequiredAsteriskTooltip';
import { IconKey } from '../../../types/system/icons';

interface OptionItem {
  value: string | number;
  label: string;
}

interface CompactSelectFieldProps {
  label?: string;
  name: string;
  value: string | number;
  onChange: (
    event: React.ChangeEvent<{ name: string; value: string | number }>
  ) => void;
  options: OptionItem[];
  placeholder?: string;
  error?: string;
  iconKey?: IconKey;
  disabled?: boolean;
  required?: boolean;
  /** Aplica mb-4. Por defecto false para filas/grillas compactas. */
  withMargin?: boolean;
  className?: string;
}

/**
 * Versión reducida de {@link SelectField}: mismo dropdown propio, colores y
 * comportamiento (foco con anillo primario, click-fuera, modo oscuro) pero con
 * altura, paddings y tipografía pensados para popovers, filtros y modales.
 */
const CompactSelectField: React.FC<CompactSelectFieldProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  placeholder = 'Seleccione una opción',
  error,
  iconKey,
  disabled,
  required,
  withMargin = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const selectedOption = options.find(
    (opt) => String(opt.value) === String(value)
  );

  // Menú desplegable "inteligente": si no cabe abajo (y sí arriba), abre hacia arriba.
  // Se renderiza en un portal (document.body) con posición "fixed" calculada
  // desde el trigger — así no lo recorta ningún contenedor con overflow
  // (p. ej. una tabla con scroll horizontal), como pasaba dentro de
  // DesktopTableInlineActions.
  const MENU_MAX_HEIGHT = 224;
  const toggleOpen = () => {
    if (disabled) return;
    setIsOpen((prev) => {
      if (!prev && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const willDropUp = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
        setDropUp(willDropUp);
        setMenuRect({
          top: willDropUp ? rect.top : rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
      return !prev;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // El menú ahora vive en un portal (document.body), fuera del árbol de
      // containerRef — hay que revisar ambos o un click en una opción se
      // trataría como "click afuera" y cerraría el menú antes de seleccionar.
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !(menuRef.current && menuRef.current.contains(target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const emit = (nextValue: string | number) => {
    onChange({
      target: { name, value: nextValue },
    } as React.ChangeEvent<{ name: string; value: string | number }>);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`relative ${withMargin ? 'mb-4' : ''} ${className}`}
    >
      {label && (
        <label
          htmlFor={name}
          className="mb-2.5 flex items-center gap-1 text-sm font-medium text-black dark:text-white"
        >
          {label}
          {required && <RequiredAsteriskTooltip delay={400} />}
        </label>
      )}

      <div
        className={`
          bg-gray-1 flex h-10 items-stretch overflow-hidden rounded-lg border
          transition-all duration-200
          focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10
          dark:border-strokedark dark:bg-meta-4
          ${error ? 'border-danger' : 'border-stroke'}
          ${disabled ? 'cursor-not-allowed opacity-70' : ''}
        `}
      >
        {iconKey && (
          <div className="flex items-center justify-center border-r border-stroke bg-gray-2 px-2.5 text-bodydark2 dark:border-strokedark dark:bg-boxdark-2">
            <InputIconByKey iconKey={iconKey} />
          </div>
        )}

        <button
          type="button"
          id={name}
          disabled={disabled}
          onClick={toggleOpen}
          className={`flex flex-1 items-center truncate bg-transparent px-3 text-left text-sm outline-none ${
            disabled
              ? 'cursor-not-allowed text-bodydark2'
              : 'cursor-pointer text-black dark:text-white'
          }`}
        >
          {selectedOption ? (
            selectedOption.label
          ) : (
            <span className="text-body dark:text-bodydark2">{placeholder}</span>
          )}
        </button>

        <span
          role="button"
          tabIndex={0}
          onClick={(event) => {
            event.stopPropagation();
            toggleOpen();
          }}
          className={`flex items-center pr-2.5 ${
            disabled ? 'pointer-events-none opacity-50' : 'cursor-pointer'
          }`}
        >
          <svg
            className={`fill-current transition-transform duration-200 ${
              isOpen ? 'rotate-180 text-primary' : 'text-body dark:text-bodydark2'
            }`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z"
            />
          </svg>
        </span>
      </div>

      {isOpen &&
        menuRect &&
        createPortal(
          <ul
            ref={menuRef}
            style={{
              position: 'fixed',
              left: menuRect.left,
              width: menuRect.width,
              top: dropUp ? undefined : menuRect.top + 4,
              bottom: dropUp ? window.innerHeight - menuRect.top + 4 : undefined,
              zIndex: 999999,
            }}
            className="max-h-52 overflow-y-auto rounded-lg border border-stroke bg-white py-1 shadow-default dark:border-strokedark dark:bg-boxdark"
          >
            {options.map((option) => (
              <li key={option.value}>
                <button
                  type="button"
                  onClick={() => emit(option.value)}
                  className={`w-full px-3 py-1.5 text-left text-sm transition hover:bg-primary hover:text-white dark:text-white ${
                    String(option.value) === String(value)
                      ? 'bg-primary/10 font-semibold text-primary'
                      : 'text-black'
                  }`}
                >
                  {option.label}
                </button>
              </li>
            ))}
            {!options.length && (
              <li className="px-3 py-2 text-sm text-bodydark2">Sin opciones</li>
            )}
          </ul>,
          document.body
        )}

      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
};

export default CompactSelectField;
